<template>
  <div v-if="edit">
    <h2 class="display-6 mb-4 d-flex align-items-center gap-1">
      <font-awesome-icon icon="fa-solid fa-people-group" />
      {{ $t('workout.format.header') }}
    </h2>
    <div class="card  p-4 mb-4">
      <div class="d-flex flex-wrap gap-1">
        <label
          v-for="option in formatList"
          :key="option"
          :for="`format-${option}`"
          class="btn"
          :class="selectedFormat === option ? 'btn-dark' : 'btn-outline-dark'">
          <input
            type="radio"
            class="btn-check"
            :id="`format-${option}`"
            :value="option"
            v-model="selectedFormat"
            autocomplete="off" />
          {{ $t(`workout.format.${option}`) }}
        </label>
      </div>
    </div>

    <h2 class="display-6 mb-4 d-flex align-items-center gap-1">
      <font-awesome-icon icon="fa-solid fa-dumbbell" />
      {{ $t('workout.focus.header') }}
    </h2>
    <div class="card  p-4 mb-4">
      <div class="d-flex flex-wrap gap-1">
        <label
          v-for="option in musclesGroup"
          :key="option"
          :for="`focus-${option}`"
          class="btn"
          :class="selectedMuscles.includes(option) ? 'btn-dark' : 'btn-outline-dark'">
          <input
            type="checkbox"
            class="btn-check"
            :id="`focus-${option}`"
            :value="option"
            v-model="selectedMuscles"
            autocomplete="off" />
          {{ $t(`workout.focus.${option}`) }}
        </label>
      </div>
    </div>
    <h2 class="display-6 mb-4 d-flex align-items-center gap-1">
      <font-awesome-icon icon="fa-solid fa-bolt" />
      {{ $t('workout.type.header') }}
    </h2>
    <div class="card  p-4 mb-0">
      <div class="row">
        <div class="col">
          <div class="d-flex flex-wrap gap-1">
            <label
              v-for="option in gymTypes"
              :key="option"
              :for="`type-${option}`"
              class="btn position-relative"
              :class="selectedGymType === option ? 'btn-dark' : 'btn-outline-dark'"
              @mouseenter="hoveredType = option"
              @focus="hoveredType = option"
              @focusout="hoveredType = null"
              @mouseleave="hoveredType = null">
              <input
                type="radio"
                class="btn-check"
                :id="`type-${option}`"
                :value="option"
                v-model="selectedGymType"
                autocomplete="off" />
              {{ $t(`workout.type.${option}.name`) }}
            </label>
          </div>
        </div>
        <div class="col" v-if="hoveredType || selectedGymType">
          <h6 class="mb-0">
            <font-awesome-icon icon="fa-solid fa-circle-info" />
            {{ $t(`workout.type.${hoveredType || selectedGymType}.aim`) }}
          </h6>
          <small class="text-body-secondary">{{ $t(`workout.type.${hoveredType || selectedGymType}.description`)
          }}</small>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="d-flex flex-wrap gap-1 mt-3 align-items-center">
    <div class="d-flex flex-wrap gap-1">
      <h1 v-for="option in selectedMuscles" :key="option" class="lead">
        <font-awesome-icon icon="fa-solid fa-chevron-right" />
        {{ $t(`workout.focus.${option}`) }}
      </h1>
    </div>
    <span v-if="selectedGymType" class="badge bg-dark d-flex align-items-center ms-auto">
      <font-awesome-icon icon="fa-solid fa-bolt" class="me-1" />
      {{ $t(`workout.type.${selectedGymType}.name`) }}
    </span>
  </div>
</template>

<script setup>
import {
  ref, watch, computed,
} from 'vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ muscles: [], type: '', format: '' }),
  },
  edit: {
    type: Boolean,
    format: String,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'update:isValid']);

const musclesGroup = ['legs', 'biceps', 'triceps', 'back', 'chest', 'glutes', 'shoulders', 'abs'];
const gymTypes = ['recovery', 'cardio', 'hypertrophy', 'strength'];
const formatList = ['self', 'coach', 'group', 'online'];

const selectedMuscles = ref([]);
const selectedGymType = ref(null);
const hoveredType = ref(null);
const selectedFormat = ref('');
const initialized = ref(false);

watch(
  () => props.modelValue,
  (newVal) => {
    if (initialized.value) return;
    selectedMuscles.value = Array.isArray(newVal.muscles) ? [...newVal.muscles] : [];
    selectedGymType.value = newVal.type || '';
    selectedFormat.value = newVal.format || '';
    initialized.value = true;
  },
  { immediate: true },
);

const isValid = computed(() => selectedMuscles.value.length > 0 && !!selectedGymType.value && !!selectedFormat.value);

watch(
  [selectedMuscles, selectedGymType, selectedFormat],
  ([muscles, type, format]) => {
    const changed = JSON.stringify(muscles) !== JSON.stringify(props.modelValue.muscles)
      || type !== props.modelValue.type
      || format !== props.modelValue.format;
    if (changed) {
      emit('update:modelValue', { muscles, type, format });
    }
    emit('update:isValid', isValid.value);
  },
  { deep: true },
);
</script>
